import { useCurrentTenant, useFrontendVersion, useIsMobile } from "app/jm";
import {
  OAuthProvider,
  signInWithEmailAndPassword,
  getAuth,
  signInWithPopup,
} from "firebase/auth";
import { app } from "app/Firebase";
import type { FormEvent } from "react";
import { useCallback, useState, useRef, useEffect } from "react";
import { Button, InputGroup } from "@blueprintjs/core";
import Toaster from "src/app/Toaster";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { z } from "zod";
import { useFirebaseTenantDownloadURL } from "./useFirebaseTenantDownloadURL";
import Logo from "src/app/jm_logo.svg?react";
import { getApiUrl } from "lib/apiUrl";
import { captureAppError } from "lib/sentry/errorReporting";

const auth = getAuth(app);

const microsoftProvider = new OAuthProvider("microsoft.com");
const genericLoginErrorMessage = "Login failed";
const credentialLoginErrorMessage =
  "Either your email or password is not correct. Please try again.";
const loginMethodErrorMessage =
  "Unable to check your login method. Please try again.";

const getFirebaseErrorCode = (error: unknown) => {
  // Firebase errors can cross mocked/browser module boundaries in tests and
  // auth SDK integrations, so prefer the stable `code` contract over
  // `instanceof FirebaseError`.
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }

  return undefined;
};

const getFirebaseLoginErrorMessage = (error: unknown) => {
  switch (getFirebaseErrorCode(error)) {
    case "auth/invalid-credential":
    case "auth/invalid-email":
    case "auth/missing-password":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return credentialLoginErrorMessage;
    case "auth/network-request-failed":
      return "Unable to contact the login service. Check your connection and try again.";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in popup. Allow popups and try again.";
    case "auth/popup-closed-by-user":
      return "Sign in was cancelled. Please try again.";
    case "auth/too-many-requests":
      return "Too many login attempts. Please wait and try again.";
  }

  return genericLoginErrorMessage;
};

export const Login = () => {
  const { tenantId, publicTenantAssetsUrl, themeColour, displayName } =
    useCurrentTenant();
  const { data: frontendVersion } = useFrontendVersion();
  const passwordTransitionRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [uiState, setUiState] = useState<
    "email" | "password" | "externalProvider"
  >("email");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();
  useEffect(() => {
    if (uiState === "password" && passwordRef.current) {
      passwordRef.current.focus();
    } else if (uiState === "email" && emailRef.current) {
      emailRef.current.focus();
    }
  }, [uiState]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      setIsSubmitting(true);
      event.preventDefault();
      const formData = new FormData(event.currentTarget);
      const object = Object.fromEntries(formData.entries());
      if (tenantId !== "jmadmin" && !auth.tenantId) {
        throw new Error("No tenantId set");
      }
      const passwordParseResult = z.string().safeParse(object.password);
      if (
        uiState === "password" &&
        (!passwordParseResult.success || passwordParseResult.data.trim() === "")
      ) {
        Toaster.error("Please enter your password");
        setIsSubmitting(false);
        return;
      }
      const emailParseResult = z.email().safeParse(object.email);
      if (!emailParseResult.success) {
        Toaster.error("Invalid email address");
        setIsSubmitting(false);
        return;
      }
      if (uiState === "email") {
        if (tenantId === "jmadmin") {
          setUiState("password");
          setIsSubmitting(false);
          return;
        }
        void fetch(
          getApiUrl(
            `/api/userAuthProvider/${encodeURIComponent(emailParseResult.data)}?tenant=${tenantId}`,
          ),
          {
            method: "GET",
          },
        )
          .then(async (response) => {
            if (!response.ok) {
              throw new Error("Network response was not ok");
            }

            const data = await response.json();
            const { authProvider } = data;

            if (authProvider === "microsoft") {
              setUiState("externalProvider");
              microsoftProvider.setCustomParameters({
                tenant: "1aa2a47f-4fd3-462d-b36d-320b5d3dfda1",
                prompt: "select_account",
                login_hint: emailParseResult.data,
              });
              return signInWithPopup(auth, microsoftProvider).catch((error) => {
                captureAppError({
                  error,
                  componentName: "Login",
                  tags: { loginStage: "microsoft", firebaseErrorCode: getFirebaseErrorCode(error) },
                });
                Toaster.error(getFirebaseLoginErrorMessage(error));
                setIsSubmitting(false);
                setUiState("email");
                return;
              });
            }

            setUiState("password");
            setIsSubmitting(false);
          })
          .catch((error) => {
            captureAppError({
              error,
              componentName: "Login",
              tags: { loginStage: "provider_lookup" },
            });
            Toaster.error(loginMethodErrorMessage);
            setIsSubmitting(false);
            setUiState("email");
          });
      } else if (uiState === "password") {
        void signInWithEmailAndPassword(
          auth,
          String(object.email),
          String(object.password),
        ).catch((error) => {
          captureAppError({
            error,
            componentName: "Login",
            tags: { loginStage: "password", firebaseErrorCode: getFirebaseErrorCode(error) },
          });
          setIsSubmitting(false);
          Toaster.error(getFirebaseLoginErrorMessage(error));
        });
      }
    },
    [tenantId, uiState],
  );

  const { data: logoUrl } = useFirebaseTenantDownloadURL(`public/logo.png`);

  return (
    <div
      className="w-full h-full bg-cover p-0 overflow-hidden bg-black bg-opacity-50 
  grid place-items-center grid-rows-[4fr_2fr_4fr_4fr] sm:grid"
    >
      <div className="h-full w-full grid place-items-center row-[2]">
        <div
          className={`bg-white/85 ${isMobile ? "min-w-[100%] h-[100%] p-3" : "min-w-[400px] rounded-3xl p-4 "} max-w-[400px]`}
        >
          <div
            className="jm-login-logo-header"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: "2em",
            }}
          >
            {tenantId === "jmadmin" ? (
              <span className="text-lg">Job Manager Admin</span>
            ) : (
              <div className="flex items-center">
                {/* <Logo
                  className={`w-8 h-8 ml-3 mr-3 bg-white rounded-full border border-white`}
                  style={{
                    color: themeColour,
                  }}
                /> */}
                <img className="jm-login-tenant-logo w-28" src={logoUrl} />
              </div>
            )}
            {/* <img
              className="jm-login-logo"
              src="assets/img/jm_logo_small.png"
              style={{ width: 160 }}
            /> */}
            <div className="flex">
              {/* <Logo
                className={`w-6 h-6 ml-3 mr-3 bg-white rounded-full border border-white`}
                style={{
                  color: themeColour,
                }}
              /> */}
              <span className={`text-xl font-semi-bold`}>Job Manager</span>
            </div>
          </div>
          {/* <hr className="mt-2" /> */}
          <form
            onSubmit={isSubmitting ? () => null : handleSubmit}
            className="flex flex-col space-y-4"
          >
            <div className="flex min-h-[30px] items-center">
              <div className="text-lg flex flex-1">Login</div>
              {uiState !== "email" && (
                <Button
                  onClick={() => {
                    setUiState("email");
                    setIsSubmitting(false);
                  }}
                  minimal
                  intent="primary"
                  text="Switch User"
                />
              )}
            </div>
            <InputGroup
              inputClassName={
                uiState === "password" || uiState === "externalProvider"
                  ? "cursor-not-allowed bg-[rgba(211,216,222,0.5)] text-[rgba(95,107,124,0.6)]"
                  : ""
              }
              inputRef={emailRef}
              type="email"
              name="email"
              placeholder="Email"
              readOnly={
                uiState === "password" ||
                uiState === "externalProvider" ||
                isSubmitting
              }
            />
            {/* {uiState === "password" && <span>Change User</span>} */}
            {/* Password Field Transition */}
            <TransitionGroup component={null}>
              {uiState === "password" && (
                <CSSTransition
                  nodeRef={passwordTransitionRef}
                  key="password"
                  timeout={500}
                  classNames={{
                    enter: "animate-growIn",
                    exit: "animate-shrinkOut",
                  }}
                  unmountOnExit
                >
                  <div ref={passwordTransitionRef}>
                    <InputGroup
                      inputClassName={
                        uiState === "password" && isSubmitting
                          ? "cursor-not-allowed bg-[rgba(211,216,222,0.5)] text-[rgba(95,107,124,0.6)]"
                          : ""
                      }
                      rightElement={
                        <Button
                          minimal={true}
                          icon={showPassword ? "eye-off" : "eye-open"}
                          onClick={() => setShowPassword((prev) => !prev)}
                        />
                      }
                      type={showPassword ? "text" : "password"}
                      readOnly={isSubmitting}
                      name="password"
                      placeholder="Password"
                      inputRef={passwordRef}
                    />
                  </div>
                </CSSTransition>
              )}
            </TransitionGroup>

            <Button
              intent="primary"
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              text={uiState === "email" ? "Continue" : "Submit"}
            />
            {/* <Button
                text="Toggle"
                onClick={() =>
                  setUiState(uiState === "email" ? "password" : "email")
                }
              /> */}

            {/* <FormGroup label="Password">
              <InputGroup type="password" name="password" />
            </FormGroup> */}

            {/* <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Button intent="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
              {tenantId !== "jmadmin" && (
                <a
                  id="forgot"
                  href="#"
                  onClick={() => {
                    console.log("To be implemented...");
                  }}
                >
                  Forgot password
                </a>
              )}
            </div> */}
          </form>
          {/* <hr className="mt-2" /> */}
          {/* {tenantId !== "jmadmin" && (
            <div className="mt-2">
              <Button
                onClick={() => {
                  if (tenantId !== "jmadmin" && !auth.tenantId) {
                    throw new Error("No tenantId set");
                  }
                  signInWithPopup(auth, microsoftProvider).catch((error) => {
                    console.log(error);
                  });
                }}
                // Need to use inline styles because Tailwind doesn't support
                // dynamic arbitrary styles
                // https://v2.tailwindcss.com/docs/just-in-time-mode#known-limitations
                style={{
                  background: themeColour,
                }}
                className="text-white w-[100%]"
                disabled={isSubmitting}
              >
                Staff & Contractor Login
              </Button>
            </div>
          )} */}
          <p className="text-gray-1 pt-2 text-center text-xs mb-0">
            Version: {frontendVersion ?? "-"}
          </p>
        </div>
      </div>
    </div>
  );
};
